import React, { useState, useEffect } from "react";
import { Tag } from "@ann/web-components";
import { UsersContainer } from "./Users.styled";
import { UserTableSection } from "./components";
import { getUserList } from "../../../../services";
import { useDebounce } from "../../../../../../../hooks";
import dayjs from "dayjs";

const Users = ({ workspaceId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState("createdAt");
  const [orderDirection, setOrderDirection] = useState("DESC");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
  });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedUserUuids, setSelectedUserUuids] = useState([]);
  
  // Debounce search keyword
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  // Fetch users from API
  const fetchUsers = async () => {
    if (!workspaceId) return;
    
    setLoading(true);
    try {
      const response = await getUserList({
        page: currentPage,
        page_size: itemsPerPage,
        order_by: orderBy,
        order_direction: orderDirection,
        search: debouncedSearchKeyword,
        tenantUuid: workspaceId, // Pass workspace tenant_uuid for filtering
      });

      if (response && response.body) {
        setUsers(response.body);
        if (response.meta) {
          setMeta({
            totalItems: response.meta.totalItems || 0,
            totalPages: response.meta.totalPages || 0,
            currentPage: response.meta.currentPage || currentPage,
            perPage: response.meta.perPage || itemsPerPage,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, orderBy, orderDirection, debouncedSearchKeyword, workspaceId]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSortChange = (selectedOption) => {
    const validOrderBy = ["createdAt", "email", "fullName", "status", "updatedAt"];
    const newOrderBy = validOrderBy.includes(selectedOption) ? selectedOption : "createdAt";
    setOrderBy(newOrderBy);
    setCurrentPage(1);
  };

  const handleOrderChange = (currentOrder) => {
    const newOrder = currentOrder === 'asc' ? 'DESC' : 'ASC';
    setOrderDirection(newOrder);
    setCurrentPage(1);
  };

  const handleSearchChange = (keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  };

  const handleSelectionChange = (cells) => {
    const userUuids = cells.map((cell) => cell.id).filter(Boolean);
    setSelectedUserUuids(userUuids);
  };

  const handleClearSelection = () => {
    setSelectedUserUuids([]);
  };

  const getAccountStatusColor = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toUpperCase() : '';
    
    switch (normalizedStatus) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'alert';
      case 'INACTIVE':
        return 'neutral';
      case 'DELETED':
        return 'error';
      case 'EXPIRED':
        return 'error';
      default:
        return 'neutral';
    }
  };

  const formatDateAdded = (dateString) => {
    if (!dateString) return "—";
    return dayjs(dateString).format("MMM DD, YYYY");
  };

  // Map API response to table data
  const tableData = users.map((user) => {
    const statusDisplay = user.status 
      ? user.status.charAt(0).toUpperCase() + user.status.slice(1).toLowerCase() 
      : "—";

    // Extract role names from roles array
    const roleNames = user.roles && user.roles.length > 0
      ? user.roles.map(role => role.roleName).join(", ")
      : "—";

    return {
      id: user.userUuid || user.id,
      name: user.fullName || user.full_name || "—",
      role: roleNames,
      email: user.email || "—",
      status: (
        <Tag
          color={getAccountStatusColor(user.status)}
          size="small"
          text={statusDisplay}
        />
      ),
      rawStatus: user.status?.toLowerCase() || "unknown", // Keep original status for logic
      isActive: user.isActive !== undefined ? user.isActive : user.is_active, // Support both camelCase and snake_case
      dateAdded: formatDateAdded(user.createdAt || user.created_at),
    };
  });

  const headers = [
    { label: 'Name', value: 'name' },
    { label: 'Role', value: 'role' },
    { label: 'Email', value: 'email' },
    { label: 'Status', value: 'status' },
    { label: 'Date Added', value: 'dateAdded' }
  ];

  const getSelectedSortOption = () => {
    const validOrderBy = ["createdAt", "email", "fullName", "status", "updatedAt"];
    return validOrderBy.includes(orderBy) ? orderBy : "createdAt";
  };

  const getOrderDirection = () => {
    return orderDirection.toLowerCase();
  };

  return (
    <UsersContainer>
      <UserTableSection
        selectedItemsCount={selectedUserUuids.length}
        selectedUserUuids={selectedUserUuids}
        onClearSelection={handleClearSelection}
        tableData={tableData}
        headers={headers}
        onSelectionChange={handleSelectionChange}
        currentPage={meta.currentPage || currentPage}
        itemsPerPage={meta.perPage || itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        totalItems={meta.totalItems || 0}
        totalPages={meta.totalPages || 0}
        loading={loading}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={getSelectedSortOption()}
        orderDirection={getOrderDirection()}
        onSearchChange={handleSearchChange}
        onRefresh={fetchUsers}
      />
    </UsersContainer>
  );
};

export default Users;
